import { createSelector } from '@reduxjs/toolkit';

import pageType from 'Addons/search/pageType';
import { havePreventedSearchChanged, waitingForAutocompleteChanged } from 'Core/actions/autocomplete';
import { assignLocation, replaceLocation } from 'Core/actions/redirect';
import type { EpicArg } from 'Core/epics/common';
import {
  autocompleteRedirectPreventedSearchSelector,
  autocompleteRedirectWaitingForResponseSelector,
  autocompleteResponseItemsSelector,
  autocompleteResponseReceivedSelector,
} from 'Core/selectors/autocomplete';
import requestConfig from 'Models/uiConfig/requestConfig';
import uiConfig from 'Models/uiConfig/uiConfig';

const redirectUrlSelector = createSelector(
  autocompleteResponseItemsSelector,
  (items) => items?.find((item) => item.redirectUrl)?.redirectUrl ?? null,
);

export const shouldSearch = {
  forbid: 'do-not-search',
  allow: 'as-you-wish',
  force: 'search',
};

interface AutocompleteRedirectResult {
  shouldSearch: string;
  actions: (
    | ReturnType<typeof waitingForAutocompleteChanged>
    | ReturnType<typeof havePreventedSearchChanged>
    | ReturnType<typeof replaceLocation>
    | ReturnType<typeof assignLocation>
    | null
    | false
  )[];
}

export default function tryAutocompleteRedirect({ action, state }: EpicArg): AutocompleteRedirectResult {
  const { type } = action;
  const checkAutocompleteRedirect =
    action.meta?.checkAutocompleteRedirect ?? action.checkAutocompleteRedirect;
  const goToSearchPage = action.meta?.goToSearchPage ?? action.goToSearchPage;

  // if a redirect has already been started
  if (type === assignLocation.type || type === replaceLocation.type) {
    // prevent next search request
    return { shouldSearch: shouldSearch.forbid, actions: [] };
  }
  if (!requestConfig.doAutocompleteRedirect) {
    return { shouldSearch: shouldSearch.allow, actions: [] };
  }

  const haveBeenWaiting = autocompleteRedirectWaitingForResponseSelector(state);

  if (!(haveBeenWaiting || checkAutocompleteRedirect)) {
    return { shouldSearch: shouldSearch.allow, actions: [] };
  }

  const autocompleteResponseReceived = autocompleteResponseReceivedSelector(state);
  const havePreventedSearch = autocompleteRedirectPreventedSearchSelector(state);
  const willGoToSearchPage = !!(goToSearchPage && uiConfig.pageType !== pageType.search);

  if (!autocompleteResponseReceived) {
    return {
      shouldSearch: willGoToSearchPage ? shouldSearch.forbid : shouldSearch.allow,
      actions: haveBeenWaiting
        ? []
        : [
            waitingForAutocompleteChanged(true),
            willGoToSearchPage && havePreventedSearchChanged(willGoToSearchPage),
          ],
    };
  }

  const stopWaitingAction = haveBeenWaiting ? waitingForAutocompleteChanged(false) : null;

  const redirectUrl = redirectUrlSelector(state);
  if (redirectUrl) {
    return {
      shouldSearch: shouldSearch.forbid,
      actions: [
        stopWaitingAction,
        (haveBeenWaiting && !havePreventedSearch ? replaceLocation : assignLocation)(redirectUrl),
      ],
    };
  }
  if (havePreventedSearch) {
    return {
      shouldSearch: shouldSearch.force,
      actions: [stopWaitingAction, havePreventedSearchChanged(false)],
    };
  }

  return { shouldSearch: shouldSearch.allow, actions: [stopWaitingAction] };
}
